import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { LeftOutlined } from '@ant-design/icons';
import { StepBar, Button, Container } from 'components/elements';
import { colors, spacer, fontType, radius, sizes } from 'settings/style';
import { Form } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { BackButton } from 'containers/singleGroup/index.styled';

interface StepItem {
  title: string;
  pageTitle: string;
  validation: string[];
  content: () => JSX.Element;
}
interface StepBarFormProps<T> {
  steps: StepItem[];
  form: FormInstance<T>;
  onFinish: (value: T) => void | Promise<void>;
  loading: boolean;
  pageTitle: string;
  onPageBack?: () => void;
}

function StepBarForm<T>({ steps, onFinish, form, loading, pageTitle, onPageBack }: StepBarFormProps<T>) {
  const [step, setStep] = useState(1);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [step]);

  const onNext = useCallback(async () => {
    try {
      const validation = steps[step - 1]?.validation;
      await form.validateFields(validation);
      if (step === steps.length) {
        form.submit();
      } else {
        setStep(step + 1);
      }
    } catch (err) {
      // err
    }
  }, [form, step, steps]);

  const onBack = useCallback(() => {
    if (step > 1) {
      setStep(step - 1);
      return;
    }
    onPageBack?.();
  }, [onPageBack, step]);

  return (
    <Container>
      <Wrapper>
        {onPageBack ? (
          <BackButton type="button" onClick={onBack}>
            <LeftOutlined />
            Back
          </BackButton>
        ) : null}
        <ProgressBarHeader>
          <HeaderTitle>{pageTitle}</HeaderTitle>
          <StepBar step={step} steps={steps} />
        </ProgressBarHeader>
        <FormWrapper>
          <Form layout="vertical" form={form} onFinish={onFinish}>
            <FormBoxWrapper>
              {steps.map((item, index) => (
                <div style={{ display: index + 1 === step ? 'block' : 'none' }} key={item.title}>
                  <StepTitle>{item.pageTitle}</StepTitle>
                  {item.content()}
                </div>
              ))}
            </FormBoxWrapper>
          </Form>
          <FormFooter>
            {step > 1 && !loading ? <Button type="default" onClick={onBack}>Back</Button> : <span />}
            <Button type="primary" onClick={onNext} loading={loading}>
              {step === steps.length ? 'Confirm' : 'Continue'}
            </Button>
          </FormFooter>
        </FormWrapper>
      </Wrapper>
    </Container>
  );
}

export default StepBarForm;

const Wrapper = styled.div`
  width: 100%;
  padding-top: ${spacer.xl};
`;

const HeaderTitle = styled.h2`
  ${fontType.lg}
  padding-bottom: ${spacer.lg};
`;

const ProgressBarHeader = styled.div`
  padding: ${spacer.xl} 0px;
`;

const FormWrapper = styled.div`
  max-width: ${sizes.md};
  margin: auto;
`;

const FormBoxWrapper = styled.div`
  background-color: ${colors.white};
  border-radius: ${radius.md};
  margin-bottom: ${spacer.lg};
`;

const FormFooter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const StepTitle = styled.p`
  ${fontType.md};
  margin-bottom: ${spacer.xl};
`;
